import { useEffect, useState } from "react";
import CookieConsent, {
  getCookieConsentValue,
  resetCookieConsentValue,
} from "react-cookie-consent";
import { Link } from "react-router-dom";
import {
  CONSENT_COOKIE_NAME,
  CookiePreferences,
  DEFAULT_PREFERENCES,
  clearPreferences,
  getStoredPreferences,
  savePreferences,
} from "@/lib/cookieConsent";

// Evento globale per riaprire il banner dal footer ("Gestisci preferenze cookie").
export const OPEN_COOKIE_BANNER_EVENT = "open-cookie-banner";

const CookieBanner = () => {
  const [showCustomize, setShowCustomize] = useState(false);
  // forza il re-render del banner dopo una revoca (react-cookie-consent
  // nasconde il banner una volta impostato il cookie: cambiando la key lo rimontiamo).
  const [bannerKey, setBannerKey] = useState(0);
  const [preferences, setPreferences] = useState<CookiePreferences>(
    () => getStoredPreferences() ?? DEFAULT_PREFERENCES
  );

  // Alla richiesta di gestione preferenze dal footer: cancella il consenso salvato
  // e riapre il pannello Personalizza con le scelte correnti.
  useEffect(() => {
    const handleOpen = () => {
      resetCookieConsentValue(CONSENT_COOKIE_NAME);
      clearPreferences();
      setPreferences(getStoredPreferences() ?? DEFAULT_PREFERENCES);
      setBannerKey((k) => k + 1);
      setShowCustomize(true);
    };
    window.addEventListener(OPEN_COOKIE_BANNER_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_COOKIE_BANNER_EVENT, handleOpen);
  }, []);

  const persist = (prefs: CookiePreferences) => {
    savePreferences(prefs);
    setPreferences(prefs);
  };

  const handleAcceptAll = () => {
    persist({ necessary: true, maps: true });
  };

  const handleDeclineAll = () => {
    persist({ necessary: true, maps: false });
  };

  const handleSavePreferences = () => {
    persist(preferences);
    // Segna il consenso come espresso: chiude il banner e ne memorizza la scelta.
    if (getCookieConsentValue(CONSENT_COOKIE_NAME) === undefined) {
      // Il banner react-cookie-consent chiude e scrive il cookie tramite Accetta/Rifiuta;
      // per il salvataggio granulare scriviamo comunque il flag di consenso.
      try {
        document.cookie = `${CONSENT_COOKIE_NAME}=true; path=/; max-age=${
          60 * 60 * 24 * 365
        }`;
      } catch {
        /* ignora */
      }
    }
    setShowCustomize(false);
    setBannerKey((k) => k + 1);
  };

  if (showCustomize) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">Personalizza Cookie</h2>
            <p className="text-gray-600 mb-6">
              Gestisci le tue preferenze sui cookie e sui servizi di terze parti. Puoi abilitare o disabilitare le categorie non necessarie.
            </p>

            <div className="space-y-4">
              {/* Necessary Cookies */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">Cookie Tecnici Necessari</h3>
                  <span className="text-sm text-gray-500">Sempre attivi</span>
                </div>
                <p className="text-sm text-gray-600">
                  Questi cookie sono essenziali per il funzionamento del sito web e non possono essere disabilitati.
                </p>
              </div>

              {/* Maps / Third-party embed */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">Mappe Google (terze parti)</h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.maps}
                      onChange={(e) => setPreferences({ ...preferences, maps: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
                <p className="text-sm text-gray-600">
                  Consente il caricamento della mappa interattiva di Google Maps nella pagina contatti. Se disattivato, la mappa non viene caricata e non vengono trasmessi dati a Google.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSavePreferences}
                className="flex-1 bg-primary text-white py-3 px-6 rounded-lg hover:bg-primary/90 transition-colors font-semibold"
              >
                Salva Preferenze
              </button>
              <button
                onClick={() => setShowCustomize(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CookieConsent
      key={bannerKey}
      location="bottom"
      buttonText="Accetta Tutti"
      declineButtonText="Rifiuta"
      enableDeclineButton
      onAccept={handleAcceptAll}
      onDecline={handleDeclineAll}
      cookieName={CONSENT_COOKIE_NAME}
      style={{
        background: "linear-gradient(135deg, #2D5016 0%, #3D6B1F 100%)",
        padding: "20px",
        alignItems: "center",
        boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
      }}
      buttonStyle={{
        background: "#fff",
        color: "#2D5016",
        fontSize: "14px",
        fontWeight: "600",
        borderRadius: "8px",
        padding: "12px 24px",
        margin: "0 8px",
      }}
      declineButtonStyle={{
        background: "#fff",
        color: "#2D5016",
        fontSize: "14px",
        fontWeight: "600",
        borderRadius: "8px",
        padding: "12px 24px",
        margin: "0 8px",
      }}
      expires={365}
    >
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="flex-1">
          <p className="text-white text-sm md:text-base">
            Utilizziamo cookie tecnici necessari e, previo tuo consenso, servizi di terze parti (mappa Google Maps nella pagina contatti). Puoi accettare, rifiutare o scegliere per categoria.{" "}
            <Link to="/privacy-policy" className="underline hover:text-white/80">
              Privacy Policy
            </Link>
            {" | "}
            <Link to="/cookie-policy" className="underline hover:text-white/80">
              Cookie Policy
            </Link>
          </p>
        </div>
        <button
          onClick={() => setShowCustomize(true)}
          className="text-white text-sm font-semibold underline hover:text-white/80 whitespace-nowrap"
        >
          Personalizza
        </button>
      </div>
    </CookieConsent>
  );
};

export default CookieBanner;
